import { Badge } from '@/components/base/badges/badges';
import { ProgressBar } from '@/components/base/progress-indicators/progress-indicators';
import type { Container } from '@/types/container';
import { useI18n } from '@/hooks/use-i18n';
import { cx } from '@/utils/cx';

interface ContainerSpreadsheetCellProps {
    container: Container;
    field: string;
    value: any;
    onClick?: () => void;
    onContainerClick?: (containerId: string) => void;
    className?: string;
}

export const ContainerSpreadsheetCell = ({
    container,
    field,
    value,
    onClick,
    onContainerClick,
    className
}: ContainerSpreadsheetCellProps) => {
    const { translateContainerStatus, translateDocumentStatus, translateRiskLevel } = useI18n();

    const renderDisplay = () => {
        switch (field) {
            case 'containerNumber':
                if (onContainerClick) {
                    return (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onContainerClick(container.id);
                            }}
                            className="font-medium text-primary hover:text-brand-secondary transition-colors text-left"
                        >
                            {container.containerNumber}
                        </button>
                    );
                }
                return <div className="font-medium text-primary">{container.containerNumber}</div>;
            
            case 'route':
                return (
                    <div className="text-sm text-tertiary">
                        {container.route.from} → {container.route.to}
                    </div>
                );
            
            case 'shippingCompany':
                return <div className="text-sm text-tertiary">{container.shippingCompany ?? '-'}</div>;
            
            case 'progress':
                return (
                    <div className="flex items-center gap-2">
                        <ProgressBar value={container.progress} min={0} max={100} />
                        <span className="text-sm text-tertiary w-12 text-right">{container.progress}%</span>
                    </div>
                );
            
            case 'eta':
                return (
                    <span className="text-sm text-tertiary">
                        {container.eta.toLocaleDateString('it-IT')}
                    </span>
                );
            
            case 'status':
                const getStatusColor = (status: string) => {
                    switch (status) {
                        case 'In Transit': return 'brand';
                        case 'Delivered': return 'success';
                        case 'Delayed': return 'error';
                        case 'At Port': return 'warning';
                        default: return 'gray';
                    }
                };
                return (
                    <Badge color={getStatusColor(container.status)} size="sm">
                        {translateContainerStatus(container.status)}
                    </Badge>
                );
            
            case 'documentStatus':
                const getDocumentStatusColor = (status: string) => {
                    switch (status) {
                        case 'Completo': return 'success';
                        case 'Pending': return 'warning';
                        case 'Error': return 'error';
                        default: return 'gray';
                    }
                };
                return (
                    <Badge color={getDocumentStatusColor(container.documentStatus)} size="sm">
                        {translateDocumentStatus(container.documentStatus)}
                    </Badge>
                );
            
            case 'riskLevel':
                const getRiskLevelColor = (riskLevel: string) => {
                    switch (riskLevel) {
                        case 'Low': return 'success';
                        case 'Medium': return 'warning';
                        case 'High': return 'error';
                        case 'Critical': return 'error';
                        default: return 'gray';
                    }
                };
                return (
                    <Badge color={getRiskLevelColor(container.riskLevel)} size="sm">
                        {translateRiskLevel(container.riskLevel)}
                    </Badge>
                );
            
            case 'value':
                return (
                    <span className="text-sm text-tertiary">
                        {container.value.currency} {container.value.amount.toLocaleString()}
                    </span>
                );
            
            default:
                return <div className="text-sm text-tertiary">{String(value ?? '-')}</div>;
        }
    };

    // Tutti i campi sono editabili tranne containerNumber, route e progress
    const isEditable = !['containerNumber', 'route', 'progress'].includes(field);

    return (
        <div
            className={cx(
                'cursor-pointer hover:bg-secondary rounded px-1 py-0.5 transition-colors',
                isEditable && 'group',
                className
            )}
            onClick={isEditable ? onClick : undefined}
            title={isEditable ? 'Click per modificare' : undefined}
        >
            {renderDisplay()}
        </div>
    );
};

